import React, { useState, useEffect } from "react";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { motion } from "framer-motion";
import { useIntersectionObserver } from "../hooks/useIntersectionObserver";
import { ChevronRight } from "lucide-react";
import {
  Workflow,
  Clock,
  LayoutDashboard,
  Users,
  Settings,
  TrendingUp,
} from "lucide-react";
import { Zap, Shield, Sparkles } from "lucide-react";

// Import integration logos
import googleLogo from "../assets/images/logos/google.svg";
import microsoftLogo from "../assets/images/logos/microsoft.svg";
import jiraLogo from "../assets/images/logos/jira.svg";
import trelloLogo from "../assets/images/logos/trello.svg";
import slackLogo from "../assets/images/logos/slack.svg";
import githubLogo from "../assets/images/logos/github.svg";
import dropboxLogo from "../assets/images/logos/dropbox.svg";
import salesforceLogo from "../assets/images/logos/salesforce.svg";
import asanaLogo from "../assets/images/logos/asana.svg";
import evernoteLogo from "../assets/images/logos/evernote.svg";
import zapierLogo from "../assets/images/logos/zapier.svg";
import hubspotLogo from "../assets/images/logos/hubspot.svg";

const IntegrationCard = ({ integration, darkMode, index }) => {
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.1 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={`p-6 rounded-lg ${
        darkMode ? "bg-gray-800" : "bg-white"
      } shadow-lg transition-all duration-300 hover:shadow-xl`}
    >
      <img
        src={integration.logo}
        alt={`${integration.name} logo`}
        className={`h-16 mx-auto mb-4 ${darkMode ? "filter invert" : ""}`}
      />
      <h3
        className={`text-xl font-semibold mb-2 text-center ${
          darkMode ? "text-white" : "text-gray-800"
        }`}
      >
        {integration.name}
      </h3>
      <p
        className={`text-center ${
          darkMode ? "text-gray-300" : "text-gray-600"
        }`}
      >
        {integration.description}
      </p>
    </motion.div>
  );
};

function IntegrationsPage() {
  const [darkMode, setDarkMode] = useState(false);
  const [logoFade, setLogoFade] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
  }, [darkMode]);

  const handleDarkModeToggle = () => {
    setLogoFade(true);
    setTimeout(() => {
      setDarkMode(!darkMode);
      setLogoFade(false);
    }, 150);
  };

  const integrations = [
    {
      name: "Google",
      logo: googleLogo,
      description: "Integrate seamlessly with Google Workspace apps",
      features: [
        "Gmail and Calendar sync",
        "Google Drive integration",
        "Google Docs collaboration",
        "Smart meeting scheduling",
      ],
    },
    {
      name: "Microsoft",
      logo: microsoftLogo,
      description: "Enhance productivity with Microsoft 365 integration",
      features: [
        "Outlook calendar and email",
        "OneDrive integration",
        "Teams collaboration",
        "Office documents sync",
      ],
    },
    {
      name: "Jira",
      logo: jiraLogo,
      description: "Optimize project management with Jira integration",
      features: [
        "Issue tracking sync",
        "Sprint planning",
        "Automated workflows",
        "Real-time updates",
      ],
    },
    {
      name: "Trello",
      logo: trelloLogo,
      description: "Better task management with Trello board sync",
      features: [
        "Board synchronization",
        "Card management",
        "Automated tasks",
        "Custom workflows",
      ],
    },
    {
      name: "Slack",
      logo: slackLogo,
      description: "Stay connected with Slack team integration",
      features: [
        "Channel notifications",
        "Message actions",
        "File sharing",
        "Command integration",
      ],
    },
    {
      name: "GitHub",
      logo: githubLogo,
      description: "Manage code repositories and projects with GitHub",
      features: [
        "PR notifications",
        "Issue tracking",
        "Code review tools",
        "Repository sync",
      ],
    },
    {
      name: "Dropbox",
      logo: dropboxLogo,
      description: "Access Dropbox files directly within Boxia",
      features: [
        "File synchronization",
        "Shared folders",
        "Version history",
        "Smart search",
      ],
    },
    {
      name: "Salesforce",
      logo: salesforceLogo,
      description: "Unify workflow with Salesforce CRM data integration",
      features: [
        "Contact management",
        "Lead tracking",
        "Deal pipeline",
        "Custom reports",
      ],
    },
    {
      name: "Asana",
      logo: asanaLogo,
      description: "Sync Asana tasks seamlessly with Boxia",
      features: [
        "Task synchronization",
        "Project timelines",
        "Team collaboration",
        "Progress tracking",
      ],
    },
    {
      name: "Evernote",
      logo: evernoteLogo,
      description: "Import Evernote notes directly into Boxia",
      features: [
        "Note synchronization",
        "Tag management",
        "Search integration",
        "Rich text support",
      ],
    },
    {
      name: "Zapier",
      logo: zapierLogo,
      description: "Connect Boxia with thousands of apps via Zapier",
      features: [
        "Custom automations",
        "Multi-app workflows",
        "Trigger actions",
        "Data mapping",
      ],
    },
    {
      name: "HubSpot",
      logo: hubspotLogo,
      description: "Integrate marketing and sales data with HubSpot",
      features: [
        "Contact sync",
        "Deal tracking",
        "Email integration",
        "Marketing automation",
      ],
    },
  ];

  return (
    <main className="flex-grow overflow-y-auto">
      <NavBar darkMode={darkMode} logoFade={logoFade} />

      {/* Hero Section */}
      <section className="relative pt-[160px]">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-5 max-w-3xl mx-auto text-center">
            <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
              Integrations
              <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
            </h1>
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Connect with Your{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                Favorite Tools
              </span>
            </h2>
            <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
              Seamlessly integrate Boxia with your existing workflow and boost
              your productivity
            </p>
          </div>
        </div>
      </section>

      {/* Integrations Grid */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {integrations.map((integration, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-6 transition-all duration-300 hover:shadow-xl"
              >
                <div className="flex items-center gap-4">
                  <img
                    src={integration.logo}
                    alt={`${integration.name} logo`}
                    className="w-12 h-12 object-contain dark:invert dark:brightness-0 dark:contrast-200"
                  />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {integration.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {integration.description}
                    </p>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  {integration.features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                    >
                      <div className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-5 max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-5xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Why Choose Boxia Integrations?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Workflow,
                title: "Streamlined Workflow",
                description:
                  "Connect all your tools for a seamless work experience",
              },
              {
                icon: Clock,
                title: "Time-Saving Automation",
                description:
                  "Automate repetitive tasks across multiple platforms",
              },
              {
                icon: LayoutDashboard,
                title: "Centralized Dashboard",
                description:
                  "Access all your data from a single unified interface",
              },
              {
                icon: Users,
                title: "Enhanced Collaboration",
                description:
                  "Work together more efficiently with integrated tools",
              },
              {
                icon: Settings,
                title: "Customizable Workflows",
                description: "Tailor integrations to fit your unique needs",
              },
              {
                icon: TrendingUp,
                title: "Scalable Solutions",
                description: "Easily add or remove integrations as you grow",
              },
            ].map((benefit, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-6 text-center"
              >
                <div className="inline-block rounded-lg bg-gradient-to-tr from-zinc-300/30 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 p-3 mb-4">
                  <benefit.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-5 max-w-3xl mx-auto text-center">
            <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
              Alpha Access
              <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
            </h1>
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Be Part of the Future of{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                Work
              </span>
            </h2>
            <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
              Join the Boxia Alpha and be among the first to experience
              Boxia's revolutionary productivity suite
            </p>

            <div className="mt-10 flex flex-col items-center gap-4">
              <a
                href="https://app.boxia.io/auth/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 text-base font-medium rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Sign Up Now
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 duration-300" />
              </a>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                 
              </span>
            </div>
          </div>

          <div className="mt-20 space-y-10">
            <div className="relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-8">
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-8 text-center">
                Trusted by Industry Leaders
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center justify-center">
                {[
                  { name: "google", logo: googleLogo },
                  { name: "microsoft", logo: microsoftLogo },
                  { name: "slack", logo: slackLogo },
                  { name: "dropbox", logo: dropboxLogo },
                  { name: "github", logo: githubLogo },
                  { name: "salesforce", logo: salesforceLogo },
                ].map((company) => (
                  <div
                    key={company.name}
                    className="flex items-center justify-center group"
                  >
                    <img
                      src={company.logo}
                      alt={`${company.name} logo`}
                      className="w-auto h-8 opacity-50 group-hover:opacity-100 transition-opacity duration-300 dark:invert dark:brightness-0 dark:contrast-200"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: Zap,
                  title: "Lightning Fast",
                  description:
                    "Experience instant response times and real-time collaboration",
                },
                {
                  icon: Shield,
                  title: "Enterprise Ready",
                  description:
                    "Bank-level security with SOC 2 Type II certification",
                },
                {
                  icon: Sparkles,
                  title: "AI Powered",
                  description:
                    "Advanced AI features to supercharge your productivity",
                },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-6 text-center"
                >
                  <div className="inline-block rounded-lg bg-gradient-to-tr from-zinc-300/30 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 p-3 mb-4">
                    <feature.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer darkMode={darkMode} />
    </main>
  );
}

export default IntegrationsPage;
